import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { MoveRightIcon } from 'lucide-react'

import { ConversionRate } from '../types'
import { Token, USD_MOCK_TOKEN } from '@/domain/types/Token'
import { ActionsViewState } from '@/features/actions/types'
import { ActionsView } from '@/features/actions/views/ActionsView'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { assets } from '@/ui/assets'
import { DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { HorizontalScroll } from '@/ui/atoms/horizontal-scroll/HorizontalScroll'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { TransactionOverview } from '@/ui/organisms/transaction-overview/TransactionOverview'
import { testIds } from '@/ui/utils/testIds'

export interface ConvertPointsViewProps {
  spk: Token
  claimableSpk: NormalizedNumber
  pointsEarned: NormalizedNumber
  season: number
  conversionRate: ConversionRate
  actionsViewState: ActionsViewState
}

export function ConvertPointsView({
  spk,
  claimableSpk,
  pointsEarned,
  conversionRate,
  season,
  actionsViewState,
}: ConvertPointsViewProps) {
  return (
    <MultiPanelDialog>
      <DialogTitle>Claim SPK Token</DialogTitle>

      <div className="flex flex-col items-center gap-2 rounded-sm bg-secondary py-8">
        <div className="typography-label-3 text-secondary">Available to claim</div>
        <div className="typography-heading-3 flex items-center gap-2">
          <img src={spk.logo} alt={spk.symbol} className="size-8" />
          <div className="text-primary">{spk.format(claimableSpk, {})}</div>
          <div className="text-secondary">{spk.symbol}</div>
        </div>
      </div>

      <TransactionOverview>
        <TransactionOverview.Row>
          <TransactionOverview.Label>Conversion rate</TransactionOverview.Label>
          <HorizontalScroll>
            <TransactionOverview.Generic>
              <div className="flex items-center gap-2">
                <div className="flex w-fit items-center gap-1.5">
                  <img src={assets.points.pointsIcon} alt="Points icon" className="size-4 shrink-0" />
                  <div className="flex items-baseline gap-1">
                    <div
                      className="typography-label-2 text-primary"
                      data-testid={testIds.points.convertPointsDialog.transactionOverview.conversionRate.pointsAmount}
                    >
                      {USD_MOCK_TOKEN.format(conversionRate.pointsAmount, {})}
                    </div>
                    <div className="typography-body-4 text-secondary">Spark Points</div>
                  </div>
                </div>
                <MoveRightIcon className="size-4 text-secondary" />
                <div className="flex items-center gap-1.5">
                  <TokenIcon token={spk} className="size-4" />
                  <div className="flex items-baseline gap-1">
                    <div
                      className="typography-label-2 text-primary"
                      data-testid={testIds.points.convertPointsDialog.transactionOverview.conversionRate.spkAmount}
                    >
                      {spk.format(conversionRate.spkAmount, {})}
                    </div>
                    <div className="typography-body-4 text-secondary">SPK Tokens</div>
                  </div>
                </div>
              </div>
            </TransactionOverview.Generic>
          </HorizontalScroll>
        </TransactionOverview.Row>

        <TransactionOverview.Row>
          <TransactionOverview.Label>Points earned</TransactionOverview.Label>
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-1.5">
              <img src={assets.points.pointsIcon} alt="Points icon" className="size-4" />
              <div className="flex items-baseline gap-1">
                <div
                  className="typography-label-2 text-primary"
                  data-testid={testIds.points.convertPointsDialog.transactionOverview.pointsEarned}
                >
                  {USD_MOCK_TOKEN.format(pointsEarned, {})}
                </div>
                <div
                  className="typography-body-4 text-secondary"
                  data-testid={testIds.points.convertPointsDialog.transactionOverview.season}
                >
                  in Season {season}
                </div>
              </div>
            </div>
          </div>
        </TransactionOverview.Row>
      </TransactionOverview>

      <ActionsView state={actionsViewState} actionsGridLayout="compact" allowChangeWhenActionsInProgress={false} />
    </MultiPanelDialog>
  )
}
